import { useNostr } from '@nostrify/react';
import { useQuery } from '@tanstack/react-query';
import type { NostrEvent } from '@nostrify/nostrify';
import { getParentEventId } from '@/lib/postUtils';

export interface ReplyNode {
  event: NostrEvent;
  children: ReplyNode[];
}

/** How many levels of replies to fetch below the focused post. */
const MAX_DEPTH = 3;
/** Max events fetched per depth level. */
const LEVEL_LIMIT = 200;
/** Cap on parent ids probed per level so huge threads don't balloon filters. */
const FRONTIER_LIMIT = 100;

/** Total number of reply nodes in a tree (all depths). */
export function countReplyNodes(nodes: ReplyNode[]): number {
  return nodes.reduce((sum, node) => sum + 1 + countReplyNodes(node.children), 0);
}

/**
 * Fetches the reply tree below a post using breadth-first relay queries: one
 * query per depth level ("who replied to any of these ids?"), then assembles
 * everything into a nested tree following NIP-10 parent markers.
 *
 * Replies deeper than MAX_DEPTH are not fetched; clicking any leaf reply
 * navigates to its own thread view, which reconstructs the full chain.
 */
export function useReplyTree(rootId: string) {
  const { nostr } = useNostr();

  return useQuery<ReplyNode[]>({
    queryKey: ['nostr', 'reply-tree', rootId],
    queryFn: async ({ signal }) => {
      const abort = AbortSignal.any([signal, AbortSignal.timeout(15_000)]);

      const known = new Set<string>([rootId]);
      const all: NostrEvent[] = [];
      let frontier: string[] = [rootId];

      for (let depth = 0; depth < MAX_DEPTH && frontier.length > 0; depth++) {
        const events = await nostr.query(
          [{ kinds: [1], '#e': frontier, limit: LEVEL_LIMIT }],
          { signal: abort },
        );

        const fresh = events.filter((ev) => !known.has(ev.id));
        for (const ev of fresh) known.add(ev.id);
        all.push(...fresh);
        frontier = fresh.slice(0, FRONTIER_LIMIT).map((ev) => ev.id);
      }

      // Group replies by their direct parent (NIP-10). Replies whose parent
      // couldn't be resolved are attached to the root when they reference it
      // (legacy positional / root-marked replies), otherwise dropped.
      const byParent = new Map<string, NostrEvent[]>();
      for (const ev of all) {
        const resolvedParent = getParentEventId(ev)?.id;
        const parentId =
          resolvedParent && known.has(resolvedParent)
            ? resolvedParent
            : ev.tags.some(([t, id]) => t === 'e' && id === rootId)
              ? rootId
              : null;
        if (!parentId) continue;
        const siblings = byParent.get(parentId) ?? [];
        siblings.push(ev);
        byParent.set(parentId, siblings);
      }

      const build = (parentId: string): ReplyNode[] =>
        (byParent.get(parentId) ?? [])
          .sort((a, b) => a.created_at - b.created_at)
          .map((event) => ({ event, children: build(event.id) }));

      return build(rootId);
    },
    enabled: !!rootId,
    staleTime: 60 * 1000,
    retry: 1,
    retryDelay: 1000,
  });
}
